import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { signalService } from '../services';
import { GreenLitBar } from './EventStatusBar';
import ShareButton from './ShareButton';
import SignalShareModal from './SignalShareModal';
import SignalText from './SignalText';
import { AvatarStack, audienceChip, reachLabel } from './SignalInvitees';
import InviteeListSheet from './InviteeListSheet';
import { openedLine } from '../utils/portalWords';

/**
 * Party Scroll v6 — a public signal in the People feed (design screen 03).
 *
 * No page, no price. Header names the originator; a mono strip states it's a
 * signal and when it expires; the text shows verbatim; one action ("I'm in").
 */

function expiresLabel(iso) {
  if (!iso) return null;
  const ms = new Date(iso).getTime() - Date.now();
  if (ms <= 0) return 'expired';
  const mins = Math.round(ms / 60000);
  if (mins < 60) return `expires in ${mins} min`;
  return `expires in ${Math.round(mins / 60)} hr`;
}

// Human-readable start–end when the host set an explicit window; else null.
// A signal with no window defaults to start=now / end=now+4h, so a future start
// or a non-4h duration reliably means a window was chosen.
export function signalWindowLabel(startIso, endIso) {
  if (!startIso) return null;
  const start = new Date(startIso);
  const end = endIso ? new Date(endIso) : null;
  const futureStart = start.getTime() > Date.now() + 2 * 60 * 1000;
  const durH = end ? (end.getTime() - start.getTime()) / 3_600_000 : 4;
  if (!futureStart && Math.abs(durH - 4) <= 0.15) return null; // default 4h window → not "set"
  const day = (d) => d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  const time = (d) => d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  if (!end) return `${day(start)} · ${time(start)}`;
  if (start.toDateString() === end.toDateString()) return `${day(start)} · ${time(start)} – ${time(end)}`;
  return `${day(start)} ${time(start)} – ${day(end)} ${time(end)}`;
}


/**
 * The listing a signal was broadcast from — "Going to Penticton Beer Week" with
 * a way to actually get to Penticton Beer Week.
 *
 * Sits directly under the note, because that's the sentence it answers. It
 * carries the event's own title rather than a bare "See the event": the signal
 * text is free-form and the sender may have written something that never names
 * the thing.
 *
 * Stops propagation — the whole note above it opens the signal, and a reader
 * aiming at this is asking for the party instead.
 */
function SourceEventLink({ event }) {
  const navigate = useNavigate();
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); navigate(`/events/${event.inviteCode}`); }}
      className="group mt-0.5 flex w-full items-stretch gap-0 border-2 border-black bg-white text-left transition-colors hover:bg-black focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-2"
    >
      {event.posterUrl && (
        <img
          src={event.posterUrl}
          alt=""
          className="h-11 w-11 flex-shrink-0 border-r-2 border-black object-cover"
          referrerPolicy="no-referrer"
        />
      )}
      <span className="flex min-w-0 flex-1 flex-col justify-center px-2.5 py-1.5">
        <span className="font-mono text-[9px] uppercase tracking-[0.12em] text-hand-muted group-hover:text-white/70">
          {event.isUpcoming ? 'The event' : 'The event · past'}
        </span>
        <span className="truncate font-mono text-[12px] font-bold uppercase tracking-[0.06em] text-black group-hover:text-white">
          {event.title}
        </span>
      </span>
      <span className="flex flex-shrink-0 items-center pr-3 font-mono text-[13px] font-bold text-black group-hover:text-white">
        →
      </span>
    </button>
  );
}

export default function SignalCard({ signal, onChange }) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [showInvitees, setShowInvitees] = useState(false);
  const [state, setState] = useState(signal.viewerState || 'open');
  const [showShare, setShowShare] = useState(false);
  const openDetail = () => navigate(`/portals/${signal.eventId}`);
  const windowLabel = signalWindowLabel(signal.dateTimeUtc, signal.expiresAtUtc);
  const expires = expiresLabel(signal.expiresAtUtc);
  const isMine = signal.isMine || state === 'host';
  const invitees = signal.invitees || [];
  const chip = audienceChip(signal);
  const invitedCount = signal.invitedCount || 0;
  // Five faces max, and the viewer's own YOU tile takes one of those slots.
  const faceCap = signal.viewerInvited ? 4 : 5;
  const faces = invitees.slice(0, faceCap);
  const overflow = Math.max(0, invitedCount - faces.length - (signal.viewerInvited ? 1 : 0));
  const inCount = signal.acceptedCount || 0;
  const countLabel = inCount > 0
    ? `${invitedCount} invited · ${inCount} in`
    : `${invitedCount} invited`;

  // Hiding is optimistic and LOCAL first. Most parents pass onChange={load}, a
  // refetch — firing that before the write lands would re-fetch a wall that still
  // contains this signal and flash it straight back. Two callers (AllEventsGrid,
  // PeoplePage) pass no onChange at all, so the card has to be able to hide
  // itself regardless. Tell the parent only once the row exists server-side.
  const handleDismiss = async () => {
    setHidden(true);
    const r = await signalService.dismiss(signal.eventId);
    if (r?.success) onChange?.();
    else setHidden(false); // put it back rather than pretend it's gone
  };

  const joined = state === 'accepted';
  const pending = state === 'applied_pending';

  const handleImIn = async () => {
    if (busy || isMine || joined) return;
    setBusy(true);
    const r = await signalService.imDown(signal.eventId);
    setBusy(false);
    if (r.success) {
      setState(r.data?.viewerState || 'accepted');
      onChange?.(r.data);
    }
  };

  let button;
  if (isMine) {
    button = (
      <button type="button" onClick={openDetail}
        className="flex h-11 w-full items-center justify-center border-2 border-black bg-white text-center font-mono text-[12px] font-bold uppercase tracking-[0.12em] text-black transition-colors hover:bg-black hover:text-white">
        Manage
      </button>
    );
  } else if (joined) {
    button = <div className="flex h-11 w-full items-center justify-center border-2 border-black bg-glitch-green text-center font-mono text-[12px] font-bold uppercase tracking-[0.12em] text-black">You&apos;re in</div>;
  } else if (pending) {
    button = <div className="flex h-11 w-full items-center justify-center border-2 border-black bg-white text-center font-mono text-[12px] font-bold uppercase tracking-[0.12em] text-black">Asked to join</div>;
  } else {
    button = (
      <button type="button" onClick={handleImIn} disabled={busy}
        className="flex h-11 w-full items-center justify-center border-2 border-black bg-black text-center font-mono text-[12px] font-bold uppercase tracking-[0.12em] text-white transition-colors hover:bg-white hover:text-black disabled:opacity-50">
        {busy ? '…' : "I'm in"}
      </button>
    );
  }

  if (hidden) return null;

  return (
    <div className="flex flex-col border-2 border-black bg-white">
      {/* Signal status bar — cyan, SIGNAL left + OPEN/CURATED right. */}
      <GreenLitBar
        label="Portal"
        rightLabel={chip ? chip.label : (signal.mode === 'curated' ? 'Curated' : 'Open')}
        rightBg={chip ? chip.fill : 'bg-white'}
        rightTextColor={chip ? (chip.text || 'text-black') : 'text-black'}
        bg=""
        textColor="text-black"
        style={{ background: '#00FFFF' }}
        size="lg"
      />
      <div className="flex items-center gap-3 border-b-2 border-dashed border-black px-3.5 py-3">
        <span
          className="flex h-7 w-7 flex-shrink-0 items-center justify-center overflow-hidden border-2 border-black font-mono text-[12px] font-bold text-black"
          style={signal.hostAvatarUrl ? undefined : { background: '#C6B8FF' }}
        >
          {signal.hostAvatarUrl
            ? <img src={signal.hostAvatarUrl} alt="" className="h-full w-full object-cover"  referrerPolicy="no-referrer"/>
            : (signal.hostDisplayName || '?').charAt(0).toUpperCase()}
        </span>
        <span className="text-[12px] leading-tight text-black"><b>{signal.hostDisplayName}</b> opened a Portal</span>
      </div>

      <div className="flex flex-1 flex-col gap-2.5 px-3.5 pb-[18px] pt-4">
        <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-hand-muted">
          Portal{windowLabel ? ` · ${windowLabel}` : (expires ? ` · ${expires}` : '')}
        </span>
        <button type="button" onClick={openDetail} className="block w-full text-left">
          <h3 className="text-[17px] leading-[1.3] text-black sm:text-[19px]">{signal.signalText ? <>“<SignalText text={signal.signalText} />”</> : openedLine(signal.hostDisplayName)}</h3>
        </button>
        {signal.sourceEvent && <SourceEventLink event={signal.sourceEvent} />}
      </div>

      {/* Invitee band — handoff direction 1B. Full-width, divided from the note by
          a 2px rule, with the host's EDIT LIST in its own right-hand cell.
          A named room shows the chip and the faces; an open room (public / town
          square) states its reach and who is already in, with no invite list —
          there isn't one to show. */}
      {chip && (
        <div className="flex items-stretch border-t-2 border-black">
          <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-3 gap-y-2 px-3.5 py-3">
            {chip.open ? (
              <span className="truncate font-mono text-[11px] uppercase tracking-[0.1em] text-hand-muted">
                {reachLabel(signal)}
              </span>
            ) : invitedCount > 0 ? (
              <>
                <AvatarStack
                  people={faces}
                  overflow={overflow}
                  showYou={signal.viewerInvited}
                  size={34}
                  onOpenList={() => setShowInvitees(true)}
                />
                <button
                  type="button"
                  onClick={() => setShowInvitees(true)}
                  className="truncate font-mono text-[11px] uppercase tracking-[0.1em] text-hand-muted underline-offset-2 hover:underline focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-2"
                >
                  {countLabel}
                </button>
              </>
            ) : (
              <span className="font-mono text-[11px] uppercase tracking-[0.1em] text-hand-muted">No one yet.</span>
            )}
          </div>
          {isMine && !chip.open && (
            <button
              type="button"
              onClick={openDetail}
              className="flex flex-shrink-0 items-center border-l-2 border-black px-4 font-mono text-[11px] font-bold uppercase tracking-[0.12em] text-black transition-colors hover:bg-black hover:text-white focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-2"
            >
              Edit list
            </button>
          )}
        </div>
      )}

      <div className="flex items-stretch gap-2.5 border-t-2 border-black px-3.5 py-3.5">
        <div className="flex min-w-0 flex-1">{button}</div>
        <ShareButton onClick={() => setShowShare(true)} sizeClass="" style={{ width: 44, height: 44 }} />
        {!isMine && (
          <button
            type="button"
            onClick={handleDismiss}
            aria-label={`Hide this Portal from ${signal.hostDisplayName}`}
            title="Not interested — hide this"
            style={{ width: 44, height: 44 }}
            className="flex flex-shrink-0 items-center justify-center border-2 border-black bg-white transition-colors hover:bg-black hover:text-white focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-2"
          >
            <svg width="14" height="14" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="square" strokeLinejoin="miter">
              <path d="M4 4l12 12M16 4L4 16" />
            </svg>
          </button>
        )}
      </div>
      {showShare && <SignalShareModal signal={signal} onClose={() => setShowShare(false)} />}
      {showInvitees && (
        <InviteeListSheet
          eventId={signal.eventId}
          invitedCount={invitedCount}
          onClose={() => setShowInvitees(false)}
        />
      )}
    </div>
  );
}
